import {FULFILLED, PENDING} from 'redux-promise-middleware';
import {SagaIterator} from 'redux-saga';
import {all, call, select, take, takeEvery} from 'redux-saga/effects';
import {EEC, pushDataLayer} from '@optimaxdev/analytics/mobile';
import {pathOr} from 'ramda';

import {QuoteItemType} from 'constants/flow/flowTypesCart';
import {ActionType} from 'constants/flow/flowTypes';
import {CART, QUOTE_ITEM} from 'constants/actions';
import {isHtoEnabled} from 'constants/flags/flags';
import {PRODUCT_TYPES} from 'constants/product';

import {productToEECCart, productToEECCartHto} from 'libs/EEC/convertData/productToEECCart';
import {prepareWizardMsProductDataToEEC} from 'libs/EEC/convertData/productPageDataToProduct';
import {sendEECCartProducts} from 'libs/impression/EECImpression';
import {getBadge, getFrameUrl, getTypePrescription} from 'libs/EEC/productToEECProduct';
import {getQuoteItems} from 'selectors/cart/cart';
import {getCartAccessoriesId} from 'selectors/cart/accessories/accessories';
import {getActiveProduct, getParentProduct} from 'selectors/product/product.js';
import {getCustomerCurrency} from 'selectors/customer/customer';
import {getAppliedCoupons} from 'selectors/quoteItems/quoteItems';
import {addAccessory} from 'pages/cart/store/actions/accessories/accessories';
import {waitForAddToCartCleared} from 'sagas/analytics/helpers/waitFor';
import {addToCartV2 as addToCartAction} from 'actions/cart/cart';
import {getWizardMs} from 'features/wizardMs/selectors/wizard/wizard';
import {getActivePackage} from 'features/wizardMs/selectors/packages/packages';
import {getActiveUsage} from 'features/wizardMs/reducers/usages/usages';
import {AddToCartType} from 'pages/cart/store/actions/addToCart/addToCartType';
import {convertPriceEEC} from 'libs/convertPrice/convertPrice';
import {getPackage} from 'libs/EEC/packageType';

/**
 * Send event "addToCart".
 * @param {QuoteItemType} product - Added product.
 * @param {string} placeOfInteraction - place of interaction
 */
export function* sendEventAddToCart(product: QuoteItemType, placeOfInteraction = ''): SagaIterator {
    if (!product) return;

    const isHTOEnabled = yield call(isHtoEnabled);
    const products = yield call(isHTOEnabled ? productToEECCartHto : productToEECCart, [product]);

    const finalProducts = products.map(finalProduct => ({
        ...finalProduct,
        price:
            product.type === PRODUCT_TYPES.CONTACT_LENSES
                ? convertPriceEEC(product.discounted_price || 0)
                : convertPriceEEC((product.discounted_price || 0) * Number(product.qty || 1)),
        dimension53: getTypePrescription(product),
        dimension18: pathOr(false, ['isPremium'], product),
        dimension20: getPackage(product),
        dimension41: getBadge(product),
        dimension44: getFrameUrl(product),
    }));

    yield call(pushDataLayer, {
        event: EEC.ADD_TO_CART,
        ecommerce: {
            currencyCode: 'USD',
            PlaceOfInteraction: placeOfInteraction || '',
            add: {products: finalProducts},
        },
    });
}

/**
 * Send EEC event analytics for the product after increasing the amount.
 *
 * @param {ActionType<any, {quoteItemId: string, qty: number}>} action - Change item qty action.
 */
export function* onQtyChange(
    action: ActionType<any, {quoteItemId: string; qty: number}>,
): SagaIterator {
    if (!action.meta) return;
    const {quoteItemId: id, qty} = action.meta;
    const quoteItems = yield select(getQuoteItems);
    const addedItem = quoteItems.find((item: QuoteItemType) => item.id === id && item.qty < qty);
    if (addedItem) yield call(sendEventAddToCart, {...addedItem, qty: qty - addedItem.qty});
}

/**
 * Send EEC event analytics for the product after added accessory.
 */
export function* onAccessoryAdd(): SagaIterator {
    const id = yield select(getCartAccessoriesId);
    if (!id) return;
    yield take(`${QUOTE_ITEM.GET_ITEMS}_${FULFILLED}`);
    const quoteItems = yield select(getQuoteItems);
    const quoteItem = quoteItems.find(
        ({product_id: productId}) => Number(productId) === Number(id),
    );
    yield call(sendEventAddToCart, quoteItem);
}

/**
 * Get cart products
 */
export function* getCartProducts(): SagaIterator {
    const items = yield select(getQuoteItems);
    const currency = yield select(getCustomerCurrency);
    const isHTOEnabled = yield call(isHtoEnabled);
    const appliedCoupons = yield select(getAppliedCoupons);

    // TODO S.Abramovich This is a temporary solution for the order of sending events
    // (addToCart should send before EEC_cart_products)
    // We shouldn't get missing fields from GET_QUOTE for addToCart event
    yield call(waitForAddToCartCleared);

    if (items.length)
        yield call(sendEECCartProducts, {
            simpleProducts: items,
            currencyCode: currency.code,
            isHTOEnabled,
            coupons: appliedCoupons,
        });
}

/**
 * Send addToCart event when using wizard ms
 */
export function* addToCartUsingWizardMs({
    payload: {data: addToCartData},
    meta: addToCartMeta,
}: ActionType<{data: {id: number; sku: string; total: number}}, AddToCartType>) {
    const [activeProduct, product, wizard, activePackage, activeUsage] = yield all([
        select(getActiveProduct),
        select(getParentProduct),
        select(getWizardMs),
        select(getActivePackage),
        select(getActiveUsage),
    ]);
    const addedProduct = yield call(prepareWizardMsProductDataToEEC, {
        activeProduct,
        product,
        wizard,
        addToCartData,
        addToCartMeta,
        activePackage,
        activeUsage,
    });
    yield call(sendEventAddToCart, addedProduct);
}

/**
 * Cart EEC analytics root saga.
 */
export default function* EECCartEvents(): SagaIterator {
    yield takeEvery(
        [`${CART.CHANGE_ITEM_QTY}_${PENDING}`, `${CART.CHANGE_LENSES_ITEM_QTY}_${PENDING}`],
        onQtyChange,
    );
    yield takeEvery(`${addAccessory}_${PENDING}`, onAccessoryAdd);
    yield takeEvery(`${QUOTE_ITEM.GET_ITEMS}_${FULFILLED}`, getCartProducts);
    yield takeEvery(`${addToCartAction}_${FULFILLED}`, addToCartUsingWizardMs);
}
